import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Zap,
  Building2,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Copy,
  CheckCheck,
  MessageSquare,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSettings } from "@/hooks/useSettings";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export default function DisplaySettings() {
  const navigate = useNavigate();
  const { settings, updateSettings } = useSettings();
  const { user, signIn } = useAuth();
  const { toast } = useToast();

  const [ingestEmailDraft, setIngestEmailDraft] = useState<string | null>(null);
  const [savingIngestEmail, setSavingIngestEmail] = useState(false);
  const [copiedIngestEmail, setCopiedIngestEmail] = useState(false);

  const currentIngestEmail = ingestEmailDraft ?? settings?.ingest_email ?? "par@trydiscorun.com";

  const handleSaveIngestEmail = async () => {
    if (!ingestEmailDraft) return;
    setSavingIngestEmail(true);
    await updateSettings({ ingest_email: ingestEmailDraft });
    setSavingIngestEmail(false);
    setIngestEmailDraft(null);
  };

  const handleCopyIngestEmail = async () => {
    await navigator.clipboard.writeText(currentIngestEmail);
    setCopiedIngestEmail(true);
    setTimeout(() => setCopiedIngestEmail(false), 2000);
  };

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords don't match.");
      return;
    }

    setIsSavingPassword(true);

    // Verify current password first
    if (user?.email) {
      const { error: verifyError } = await signIn(user.email, currentPassword);
      if (verifyError) {
        setPasswordError("Current password is incorrect.");
        setIsSavingPassword(false);
        return;
      }
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setPasswordError(error.message);
    } else {
      toast({
        title: "Password updated",
        description: "Your password has been changed successfully.",
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    }

    setIsSavingPassword(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="w-full px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-primary">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-foreground">Display Settings</h1>
                <p className="text-sm text-muted-foreground">Customize how your company appears</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-2xl space-y-6">
        <Card className="glass-card p-6">
          <h2 className="text-lg font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Company Information
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="buyerName">Company Name</Label>
              <Input
                id="buyerName"
                type="text"
                value={settings?.buyer_name || ""}
                onChange={(e) => updateSettings({ buyer_name: e.target.value })}
                placeholder="Your company name"
                className="bg-background"
              />
              <p className="text-xs text-muted-foreground">Shown to suppliers in discount offers</p>
            </div>
          </div>
        </Card>

        {/* Ingest Email */}
        <Card className="glass-card p-6">
          <h2 className="text-lg font-display font-semibold text-foreground mb-1 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Invoice Ingest Email
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Forward or CC this address when you receive AP invoices. DiscoRun will automatically
            parse any CSV attachment and create a new backtest run.
          </p>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="ingestEmail">Ingest Address</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="ingestEmail"
                  type="email"
                  value={currentIngestEmail}
                  onChange={(e) => setIngestEmailDraft(e.target.value)}
                  placeholder="par@trydiscorun.com"
                  className="bg-background flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyIngestEmail}
                  title="Copy address"
                >
                  {copiedIngestEmail ? (
                    <CheckCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure this as a webhook destination in your Resend dashboard under{" "}
                <span className="font-medium">Inbound → Add domain</span>.
              </p>
            </div>
            {ingestEmailDraft !== null && (
              <Button
                type="button"
                size="sm"
                disabled={savingIngestEmail}
                onClick={handleSaveIngestEmail}
              >
                {savingIngestEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save Ingest Address"
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Negotiation Defaults */}
        <NegotiationDefaultsCard
          settings={settings as unknown as Record<string, unknown> | null}
          updateSettings={updateSettings as (updates: Record<string, unknown>) => Promise<void>}
        />

        <Card className="glass-card p-6">
          <h2 className="text-lg font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            Security
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-background pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                  aria-label={showCurrent ? "Hide password" : "Show password"}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-background pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-background pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {passwordError && <p className="text-sm text-destructive">{passwordError}</p>}

            <Button type="submit" disabled={isSavingPassword}>
              {isSavingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating…
                </>
              ) : (
                "Update Password"
              )}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}

// ─── Negotiation Defaults sub-card ───────────────────────────────────────────

interface NegotiationDefaultsCardProps {
  settings: Record<string, unknown> | null;
  updateSettings: (updates: Record<string, unknown>) => Promise<void>;
}

function NegotiationDefaultsCard({ settings, updateSettings }: NegotiationDefaultsCardProps) {
  const defaultTone = (settings?.default_negotiation_tone as string | null | undefined) ?? "";
  const defaultMaxApr =
    settings?.default_max_negotiation_apr != null
      ? String(settings.default_max_negotiation_apr)
      : "";

  const [tone, setTone] = useState(defaultTone);
  const [maxApr, setMaxApr] = useState(defaultMaxApr);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await updateSettings({
      default_negotiation_tone: tone || null,
      default_max_negotiation_apr: maxApr ? Number(maxApr) : null,
    });
    setIsSaving(false);
  };

  return (
    <Card className="glass-card p-6">
      <h2 className="text-lg font-display font-semibold text-foreground mb-1 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-primary" />
        Negotiation Defaults
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Pre-fill these values when starting a new vendor negotiation.
      </p>
      <form onSubmit={handleSave} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="defaultMaxApr">Default Max Acceptable APR (%)</Label>
          <Input
            id="defaultMaxApr"
            type="number"
            min="0"
            step="0.1"
            value={maxApr}
            onChange={(e) => setMaxApr(e.target.value)}
            placeholder="18"
            className="bg-background max-w-xs"
          />
          <p className="text-xs text-muted-foreground">
            The highest APR you are willing to accept in a negotiation.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="defaultTone">Default Negotiation Tone</Label>
          <Textarea
            id="defaultTone"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            placeholder="Professional and collaborative. Emphasise long-term partnership."
            rows={3}
            className="bg-background resize-none"
          />
          <p className="text-xs text-muted-foreground">
            Used as a prompt hint when the AI drafts negotiation emails.
          </p>
        </div>
        <Button type="submit" size="sm" disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : (
            "Save Negotiation Defaults"
          )}
        </Button>
      </form>
    </Card>
  );
}
